<script>
import baseMixin from '@/mixins/baseMixin'
import timesheetMixin from '@/mixins/timesheetMixin'
import { CandidateTimesheetChangeType } from '@/shared/constants/timesheet/CandidateTimesheetChangeType'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import dayjs from '@/services/date'
import TimesheetsChangeLabelModel from '@/models/timesheets/timesheetChangeLabelModel'
import { BookingStatus } from '@/shared/constants/booking/BookingStatus'

export default {
  name: 'TimesheetCandidateChangesLabel',
  mixins: [baseMixin, timesheetMixin],
  props: {
    timesheet: {
      type: Object,
      required: true,
    },
  },
  computed: {
    candidateChangeLabelObject() {
      return this.timesheet.candidateBookingModifications
    },
    getPriorityChange() {
      // Both shift and delta are mutually exclusive
      return this.candidateChangeLabelObject.changes.find((change) =>
        [
          CandidateTimesheetChangeType.delta,
          CandidateTimesheetChangeType.shift,
        ].includes(change.type)
      )
    },
    getBreaksChange() {
      return this.candidateChangeLabelObject.changes.find(
        (change) => change.type === CandidateTimesheetChangeType.unpaidBreaks
      )
    },
    hasBreaksChange() {
      return !!this.getBreaksChange
    },
    compFormattedOriginalStartTime() {
      return this.renderLocalDateToTime(
        this.timesheet.startTimeLocal,
        DateFormatToken.hhmma
      )
    },
    compFormattedOriginalEndTime() {
      return this.renderLocalDateToTime(
        this.timesheet.endTimeLocal,
        DateFormatToken.hhmma
      )
    },
    compFormattedModifiedStartTime() {
      return dayjs(
        this.candidateChangeLabelObject.startTime,
        DateFormatToken.HHmm
      ).format(DateFormatToken.hhmma)
    },
    compFormattedModifiedEndTime() {
      return dayjs(
        this.candidateChangeLabelObject.endTime,
        DateFormatToken.HHmm
      ).format(DateFormatToken.hhmma)
    },
    compOriginalBreakMinutes() {
      return this.timesheet.breakMinutes
    },
    compModifiedBreakMinutes() {
      return this.candidateChangeLabelObject.unpaidBreakMinutes
    },
    compCandidateModifiedChangeObj() {
      const changeLabelObject = new TimesheetsChangeLabelModel({})

      const change = this.getPriorityChange || this.getBreaksChange

      const humanReadableDuration = this.$t('timesheets.dispute.deltaMins', [
        Math.abs(change.differenceInMinutes),
      ])

      const isDifferencePositive = this.isDifferencePositive(
        change.differenceInMinutes
      )

      switch (change.type) {
        case CandidateTimesheetChangeType.delta:
          changeLabelObject.icon = isDifferencePositive
            ? 'mdi-plus'
            : 'mdi-minus'
          changeLabelObject.tooltip = this.generateDeltaTooltipText(change)
          changeLabelObject.text = humanReadableDuration
          break
        case CandidateTimesheetChangeType.shift:
          changeLabelObject.icon = isDifferencePositive
            ? 'mdi-arrow-collapse-right'
            : 'mdi-arrow-collapse-left'
          changeLabelObject.tooltip = this.generateShiftedChangeTooltipText(
            change
          )
          changeLabelObject.text = humanReadableDuration
          break
        case CandidateTimesheetChangeType.unpaidBreaks:
          changeLabelObject.icon = 'mdi-coffee-outline'
          changeLabelObject.tooltip = this.generateBreaksTooltipText(change)
          changeLabelObject.text = `${
            isDifferencePositive ? '+' : '-'
          }${humanReadableDuration}`
          break
        default:
          changeLabelObject.icon = 'mdi-help-circle-outline'
          changeLabelObject.classList = ''
          changeLabelObject.tooltip = this.$t(
            'timesheets.dispute.unknownLabelTooltipText'
          )
          changeLabelObject.text = this.$t(
            'timesheets.dispute.unknownLabelText'
          )
      }

      return changeLabelObject
    },
    compChangeLabelObject() {
      let changeLabelObject = new TimesheetsChangeLabelModel({})

      switch (this.timesheet.bookingStatus) {
        case BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED: {
          changeLabelObject = this.compCandidateModifiedChangeObj
          break
        }
        case BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN: {
          changeLabelObject.icon = 'mdi-alert-circle-outline'
          changeLabelObject.iconClass = 'mr-1'
          changeLabelObject.classList = ''
          changeLabelObject.text = this.$t(
            'timesheets.dispute.didNotHappenLabelText'
          )
          changeLabelObject.tooltip = this.$t(
            'timesheets.dispute.didNotHappenLabelTooltipText',
            [this.timesheet.candidateFirstName]
          )
          break
        }
        default: {
          changeLabelObject.isVisible = false
        }
      }

      return changeLabelObject
    },
    hasTooltipText() {
      return !!this.compChangeLabelObject.tooltip
    },
  },
  methods: {
    generateShiftedChangeTooltipText(change) {
      let tooltipText = this.$t(
        `timesheets.dispute.${
          this.isDifferencePositive(change.differenceInMinutes)
            ? 'shiftedLabelForwardTooltipText'
            : 'shiftedLabelBackwardTooltipText'
        }`,
        [
          this.differenceInMinutesAsPositiveInt(change.differenceInMinutes),
          this.compFormattedOriginalStartTime,
          this.compFormattedModifiedStartTime,
          this.compFormattedOriginalEndTime,
          this.compFormattedModifiedEndTime,
        ]
      )

      if (this.hasBreaksChange) {
        tooltipText += `\n\n${this.generateBreaksTooltipText()}`
      }

      return tooltipText
    },
    generateDeltaTooltipText(change) {
      const hasStartTimeBeenModified =
        this.compFormattedOriginalStartTime !==
        this.compFormattedModifiedStartTime

      const hasEndTimeBeenModified =
        this.compFormattedOriginalEndTime !== this.compFormattedModifiedEndTime

      let tooltipText = this.$t(
        `timesheets.dispute.${
          this.isDifferencePositive(change.differenceInMinutes)
            ? 'deltaMoreLabelTooltipDescriptionText'
            : 'deltaLessLabelTooltipDescriptionText'
        }`,
        [this.differenceInMinutesAsPositiveInt(change.differenceInMinutes)]
      )

      if (hasStartTimeBeenModified) {
        tooltipText += this.$t(
          'timesheets.dispute.deltaLabelTooltipStartTimeChangeText',
          [
            this.compFormattedOriginalStartTime,
            this.compFormattedModifiedStartTime,
          ]
        )
      }

      if (hasEndTimeBeenModified) {
        tooltipText += this.$t(
          'timesheets.dispute.deltaLabelTooltipEndTimeChangeText',
          [this.compFormattedOriginalEndTime, this.compFormattedModifiedEndTime]
        )
      }

      if (this.hasBreaksChange) {
        tooltipText += `\n\n${this.generateBreaksTooltipText()}`
      }

      return tooltipText
    },
    generateBreaksTooltipText() {
      const difference = this.differenceInMinutesAsPositiveInt(
        this.getBreaksChange.differenceInMinutes
      )

      const tooltipText = this.isDifferencePositive(
        this.getBreaksChange.differenceInMinutes
      )
        ? this.$t('timesheets.dispute.breakMinutesMoreTooltip', [
            this.timesheet.candidateFirstName,
            difference,
            this.compOriginalBreakMinutes,
            this.compModifiedBreakMinutes,
          ])
        : this.$t('timesheets.dispute.breakMinutesLessTooltip', [
            this.timesheet.candidateFirstName,
            difference,
            this.compOriginalBreakMinutes,
            this.compModifiedBreakMinutes,
          ])

      return tooltipText
    },
  },
}
</script>

<template>
  <div
    v-if="compChangeLabelObject.isVisible"
    :class="hasTooltipText ? 'cursor-help' : ''"
  >
    <v-tooltip :disabled="!hasTooltipText" top>
      <template v-slot:activator="{ on, attrs }">
        <p
          data-testid="timesheet-candidate-changes-label"
          v-bind="attrs"
          :class="[
            'mb-0 caption d-flex align-center ws-pre-wrap',
            compChangeLabelObject.classList,
          ]"
          v-on="on"
        >
          <v-icon
            v-if="compChangeLabelObject.icon"
            data-testid="timesheet-candidate-changes-label-icon"
            size="14"
            :class="[compChangeLabelObject.iconClass]"
            >{{ compChangeLabelObject.icon }}</v-icon
          >
          <span data-testid="timesheet-candidate-changes-label-text">{{
            compChangeLabelObject.text
          }}</span>
        </p>
      </template>
      <span class="ws-pre-wrap">{{ compChangeLabelObject.tooltip }}</span>
    </v-tooltip>
  </div>
</template>
